import { useEffect, useRef } from 'react';
import useAuth from 'hooks/useAuth';
import useNotificationsStore from './notificationsStore';
import { useApiFetcher } from 'utils/fetcher/ApiFetcher';

const POLL_INTERVAL_MS = 120000;

/**
 * useNotificationsPoller — Component-independent polling hook.
 * Mount once in DashboardLayout. Activates when customVars.whatsappEnabled
 * or customVars.notificationsEnabled is true.
 * Calls GetNotifications every 120s and updates the notifications store.
 */
const useNotificationsPoller = () => {
  const { user } = useAuth();
  const apiFetcher = useApiFetcher();
  const fetchHeaderCounters = useNotificationsStore(state => state.fetchHeaderCounters);
  const initializeFromUserData = useNotificationsStore(state => state.initializeFromUserData);
  const initStartedRef = useRef(false);

  const isEnabled = !!(user?.customVars?.whatsappEnabled || user?.customVars?.notificationsEnabled);

  useEffect(() => {
    if (!user?.id || !isEnabled) return;
    if (initStartedRef.current) return;

    initStartedRef.current = true;

    // Hydrate from JWT initData first to avoid an immediate API call
    const initialized = initializeFromUserData(user?.initData?.notificationsList);
    if (!initialized) {
      fetchHeaderCounters(apiFetcher);
    }

    const intervalId = setInterval(() => {
      fetchHeaderCounters(apiFetcher);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isEnabled]);
};

export default useNotificationsPoller;
